import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { IconEnum as Icons } from '../Icons';
import BaseModal, { ModalList } from '../modals/BaseModal';
import {
  GET_SU_DASHBOARD_STAT_CONTACTS,
  GET_REFFERED_CONTACTS,
  GET_SUPERADMIN_CONTACTS,
} from '../../graphql/queries';
import CustomerTable from './CustomerTable';
import { AppContext } from '../../context/AppContext';
import * as Price from '../../util/Price';
import Section from '../../shared/Section';
import Container from '../../shared/Container';
import { useMobileMedia } from '../../util/MediaQuery';
import ContactsList from './mobile_ui/ContactsList';
import Icon from '../Icon';
import colors from '../../styles/colors';
import FilterBar from './FilterBar';

const BreadcrumbContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.justify || 'flex-start'};
  background: white;
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }
  @media screen and (max-width: 450px) {
    padding: 15px 0 15px 10px;
  }
`;

const BreadcrumbWrapper = styled.ul`
  display: flex;
  align-items: center;
  list-style-type: none;
  max-height: 20px;
  align-items: center;
`;

const BreadCrumbItem = styled.li`
  display: inline-flex;
  align-items: center;
  margin: 0 0 0 10px;
  font-size: 13px;
  cursor: ${(props) => (props.clickable === '1' ? 'pointer' : null)};
  color: ${(props) => (props.clickable === '1' ? colors.seaGreen : null)};

  & :first-child {
    margin: 0;
  }
`;

const BreadcrumbHeading = styled.li`
  font-size: 15px;
  padding: 0 10px 0 10px;
  position: sticky;
  left: -15px;
  background: white;
  cursor: pointer;
  z-index: 5;
  align-items: center;
  display: flex;
  @media screen and (max-width: 450px) {
    left: -15px;
    padding: 0 10px 0 10px;
  }
`;

const BreadcrumbClearButton = styled.li`
  font-size: 15px;
  padding: 0 25px 0 10px;
  position: sticky;
  right: -16px;
  background: white;
  cursor: pointer;
  z-index: 5;
  align-items: center;
  display: flex;
  @media screen and (max-width: 450px) {
    right: 0;
    padding: 0 5px 0 10px;
  }
`;

// Csv file header
const headers = [
  { label: 'PHONE', key: 'phoneNumber' },
  { label: 'CREATED AT', key: 'createdAt' },
  { label: 'LAST IMPORT', key: 'lastImported' },
  { label: 'CURRENT AVAILABLE BALANCE', key: 'currentAvailableBalance' },
  { label: 'TOTAL CREDIT GRANTED', key: 'totalCreditGranted' },
  { label: 'TOTAL CREDIT REDEEMED', key: 'totalCreditRedeemed' },
  { label: 'TOTAL DISCOUNTS GRANTED', key: 'totalCouponsGranted' },
  { label: 'TOTAL DISCOUNTS REDEEMED', key: 'totalCouponsRedeemed' },
  { label: 'TOTAL REF DISCOUNTS ACCEPTED', key: 'totalCouponsAcceptedRef' },
  { label: 'TOTAL REF DISCOUNTS REDEEMED', key: 'totalCouponsRedRef' },
  { label: 'TOTAL REF CONVERSION RATE', key: 'totalConversionRateRef' },
  { label: 'ORIGINAL SOURCE', key: 'originalSource' },
  { label: 'OPT-IN METHOD', key: 'optInMethod' },
  { label: 'LAST OPT-IN', key: 'lastOptIn' },
  { label: 'OPT-OUT', key: 'optOut' },
];

export const initialFilterData = {
  phoneNumber: '',
  organizationId: '',
  credits: {
    mustBe: '>=',
    amount: '',
    type: 'totalCreditGranted',
  },
  originalSource: '',
  selectedDate: null,
  optInMethod: '',
  timestamp: {
    mustBe: '>=',
    type: 'createdAt',
    value: '',
  },
};

export default function SuperAdminContacts() {
  const { app, setApp } = useContext(AppContext);
  const [contactModalIsVisible, setContactModalIsVisible] = useState(false);
  const [searchModalIsVisible, setSearchModalIsVisible] = useState(false);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [isMore, setIsMore] = useState(true);
  const [allContactsData, setAllContactsData] = useState([]);
  const [allReferredContacts, setAllReferredContacts] = useState([]);
  const [filteredContactsData, setFilteredContactsData] = useState([]);
  const [contactRefFilter, setContactRefFilter] = useState({});
  const [referrers, setReferrers] = useState([]);
  const [allContactsLoading, setAllContactsLoading] = useState(false);
  const isMobile = useMobileMedia();
  const [fetching, setFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState({ type: 'allContacts' });
  const [filterData, setFilterData] = useState(initialFilterData);
  const [scrollToTop, setScrollToTop] = useState(true);
  const [allMetricsContacts, setAllMetricsContacts] = useState([]);

  const [ordering, setOrdering] = useState({
    fieldName: 'phoneNumber',
    order: 'asc',
  });

  const [filterApplied, setFilterApplied] = useState(false);
  const csvRef = useRef();
  const breadcrumbRef = useRef();

  const pagination = {
    limit: 15,
    offset: 1,
  };

  const [getContacts, { data, loading, fetchMore }] = useLazyQuery(
    GET_SUPERADMIN_CONTACTS,
    {
      fetchPolicy: 'network-only',
      onCompleted() {
        setFilterApplied(true);
        setApp((oldApp) => ({ ...oldApp, refetchContacts: getContacts }));
      },
    },
  );

  const [
    getRefferedContacts,
    { data: refData, refFetchMore, loading: refLoading },
  ] = useLazyQuery(GET_REFFERED_CONTACTS, {
    variables: {
      ...contactRefFilter,
    },
    onCompleted() {
      setApp((oldApp) => ({
        ...oldApp,
        paginationOffset: 1,
      }));
      setFilterApplied(true);
    },
  });

  const [
    getSuperAdminDashboardStatContacts,
    { data: statContacts, loading: statLoading },
  ] = useLazyQuery(GET_SU_DASHBOARD_STAT_CONTACTS, {
    onCompleted() {
      setApp((oldApp) => ({
        ...oldApp,
        paginationOffset: 1,
        getRefsQuery: false,
      }));

      setFetching(false);
    },
  });

  // Download csv file
  // const downloadExportData = () => {
  //   if (allContactsData?.length > 0) {
  //     csvRef.current.link.click();
  //   }
  // };

  useEffect(() => {
    if (
      exportData.length > 0
      && csvRef
      && csvRef.current
      && csvRef.current.link
    ) {
      setTimeout(() => {
        csvRef.current.link.click();
        setExportData([]);
      });
    }
  }, [exportData]);

  // Perform sorting client side
  function dynamicsort(property, order) {
    let sort_order = 1;
    if (order === 'desc') {
      sort_order = -1;
    }
    return function (a, b) {
      // a should come before b in the sorted order
      if (a[property] < b[property]) {
        return -1 * sort_order;
        // a should come after b in the sorted order
      }
      if (a[property] > b[property]) {
        return 1 * sort_order;
        // a and b are the same
      }
      return 0 * sort_order;
    };
  }

  const handleSort = (columnName) => {
    setApp((old) => ({ ...old, paginationOffset: 1 }));
    let contacts = [];
    if (referrers.length > 0) {
      contacts = refData?.getReferedContacts || [];
    } else if (app.getSuMetricesQuery) {
      contacts = statContacts?.getSuperAdminDashboardStatContacts || [];
    } else {
      contacts = data?.getSuperAdminContacts || [];
    }
    const newOrderDirection = ordering.order === 'asc' ? 'desc' : 'asc';
    const filter = contacts.sort(dynamicsort(columnName, newOrderDirection));

    setFilteredContactsData(
      paginate(filter, pagination.limit, pagination.offset),
    );

    setOrdering({ fieldName: columnName, order: newOrderDirection });
    setScrollToTop(!scrollToTop);
    setIsMore(true);
  };

  // Perform paging client side
  function setPaginate(array, page_size, page_number) {
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  const paginate = useCallback(
    (array, page_size, page_number) => {
      return setPaginate(array, page_size, page_number);
    },

    [],
  );

  const initialStatePaging = useCallback(
    (contacts) => {
      setOrdering({ fieldName: 'phoneNumber', order: 'asc' });
      setApp((old) => ({ ...old, paginationOffset: 1 }));
      const filter = contacts.sort(
        dynamicsort(ordering.fieldName, ordering.order),
      );
      setFilteredContactsData(
        paginate(filter, pagination.limit, pagination.offset),
      );
      setIsMore(true);
    },
    [paginate, pagination.limit, pagination.offset, setApp] // eslint-disable-line
  );

  // Load moredata client side.
  const loadMoreData = () => {
    setApp({ ...app, paginationOffset: app.paginationOffset + 1 });
    let newData = [];
    if (referrers.length > 0) {
      newData = paginate(
        allReferredContacts,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    } else if (app.getSuMetricesQuery) {
      newData = paginate(
        allMetricsContacts,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    } else {
      newData = paginate(
        allContactsData,
        pagination.limit,
        app?.paginationOffset + 1,
      );
    }
    if (newData.length > 0) {
      const appendData = [...filteredContactsData, ...newData];
      setFilteredContactsData(appendData);
    } else {
      setIsMore(false);
    }
    setIsLoadingMoreData(true);
  };

  const getRefferedContactsClick = (props) => {
    setSearchQuery({});
    setContactRefFilter(() => app?.getRefsQuery || props);
    getRefferedContacts();
    setReferrers((oldReferrers) => [
      ...oldReferrers,
      app?.getRefsQuery || props,
    ]);
    const breadCrumbContainer = breadcrumbRef?.current;
    if (breadCrumbContainer) {
      breadCrumbContainer.scrollLeft = breadCrumbContainer?.scrollWidth;
    }
  };

  const onBreadCrumbItemClick = (phoneNumber, couponType, i) => {
    setSearchQuery({});
    setContactRefFilter((oldData) => ({ ...oldData, phoneNumber, couponType }));
    getRefferedContacts();
    setReferrers((oldReferrers) => [
      ...oldReferrers.filter((ref, ind) => ind <= i),
    ]);
  };

  const clearBreadcrumb = () => {
    setReferrers([]);
    setContactRefFilter({});
    setAllContactsData([]);
    setFilteredContactsData([]);
    setApp((oldApp) => ({ ...oldApp, getSuMetricesQuery: false }));
    setFilterApplied(false);
  };

  useEffect(() => {
    setApp((oldApp) => ({
      ...oldApp,
      searchQuery: {},
      paginationOffset: 1,
    }));
  }, [setApp]);

  useEffect(() => {
    if (searchQuery) {
      if (searchQuery.type === 'allContacts') {
        const appliedQuery = searchQuery.searchQuery || null;
        if (!appliedQuery || Object.keys(appliedQuery).length < 1) {
          return;
        }
        setApp((oldApp) => ({
          ...oldApp,
          searchQuery: appliedQuery,
          paginationOffset: 1,
        }));
        getContacts({
          variables: {
            query: appliedQuery,
            paginationOffset: 1,
          },
        });
      }
    }
  }, [getContacts, searchQuery, setApp]);

  useEffect(() => {
    if (searchQuery) {
      if (searchQuery.type === 'clear' && referrers.length < 1) {
        setFilterApplied(false);
        setApp((old) => ({ ...old, searchQuery: {} }));
      }
    }
  }, [searchQuery, setApp, referrers]);

  useEffect(() => {
    if (loading && !(searchQuery.type === 'clear')) {
      setAllContactsLoading(true);
    } else {
      setAllContactsLoading(false);
    }
  }, [loading, searchQuery]);

  useEffect(() => {
    setFetching(false);
  }, [filteredContactsData]);

  useEffect(() => {
    setContactRefFilter({});
    if (app?.getSuMetricesQuery) {
      setApp((oldApp) => ({
        ...oldApp,
        searchQuery: { ...app?.getSuMetricesQuery },
      }));

      const {
        type, subType, start, end,
      } = app.getSuMetricesQuery;

      getSuperAdminDashboardStatContacts({
        variables: {
          query: {
            type,
            subType,
            start,
            end,
          },
        },
      });
    }

    return () => {
      setTimeout(() => {
        setApp((oldApp) => ({
          ...oldApp,
          paginationOffset: 1,
          getRefsQuery: false,
          getSuMetricesQuery: false,
        }));
      }, 0);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    setFetching(statLoading);
  }, [statLoading]);

  useEffect(() => {
    if (data) {
      const allContacts = data?.getSuperAdminContacts || [];
      initialStatePaging(allContacts);
      setAllContactsData(allContacts);
    }
  }, [data, initialStatePaging]);

  useEffect(() => {
    if (statContacts) {
      const metricsContacts = statContacts?.getSuperAdminDashboardStatContacts || [];
      initialStatePaging(metricsContacts);
      setAllMetricsContacts(metricsContacts);
      setFilterApplied(true);
      setFetching(false);
    }
  }, [statContacts, initialStatePaging]);

  useEffect(() => {
    if (refData) {
      const refContacts = refData?.getReferedContacts || [];
      initialStatePaging(refContacts);
      setAllReferredContacts(refContacts);
      setFetching(false);
    }
  }, [refData, initialStatePaging]);

  useEffect(() => {
    if (referrers.length > 0) {
      setApp((oldApp) => ({ ...oldApp, getSuMetricesQuery: false }));
    }
  }, [referrers, setApp]);

  return (
    <>
      <BaseModal
        open={contactModalIsVisible}
        modalType={ModalList.ImportContactsModal}
        onClose={setContactModalIsVisible}
        setSearchQuery={setSearchQuery}
        setReferrers={setReferrers}
      />
      <BaseModal
        open={searchModalIsVisible}
        modalType={ModalList.SearchModal}
        onClose={setSearchModalIsVisible}
        referrers={referrers}
        setSearchQuery={setSearchQuery}
      />
      <Container>
        <Section
          headerTitle="Superadmin Customers"
          headerDirection={isMobile ? 'column' : 'row'}
          // toolbarItems={getToolBarButtons()}
          contentDirection="column"
        >
          <CSVLink
            data={allContactsData.map((row) => {
              return {
                phoneNumber: row.phoneNumber,
                createdAt: moment(row.createdAt * 1000).format('L'),
                lastImported: moment(row.lastImported * 1000).format('L'),
                discount: `$${Price.output(row.currentAvailableBalance, true)}`,
                totalCreditGranted: `$${Price.output(
                  row.totalCreditGranted,
                  true,
                )}`,
                totalCreditRedeemed: `$${Price.output(
                  row.totalCreditRedeemed,
                  true,
                )}`,
                totalCouponsGranted: row.totalCouponsGranted,
                totalCouponsRedeemed: row.totalCouponsRedeemed,
                totalCouponsAcceptedRef: row.totalCouponsAcceptedRef,
                totalCouponsRedRef: row.totalCouponsRedRef,
                totalConversionRateRef: `${row.totalConversionRateRef}%`,
                originalSource: row.originalSource,
                optInMethod: row.optInMethod,
                lastOptIn: row.lastOptIn,
                optOut: row.optOut,
              };
            })}
            headers={headers}
            filename="Contacts.csv"
            ref={csvRef}
            target="_self"
          />
          {referrers.length < 1 && !app?.getSuMetricesQuery && (
            <FilterBar
              getContacts={getContacts}
              setFilterApplied={setFilterApplied}
              filterData={filterData}
              setFilterData={setFilterData}
              referrers={referrers}
              setSearchQuery={setSearchQuery}
              filterApplied={filterApplied}
            />
          )}
          {referrers.length > 0 && (
            <BreadcrumbContainer ref={breadcrumbRef}>
              <BreadcrumbWrapper>
                <BreadcrumbHeading>Referrers:</BreadcrumbHeading>

                {referrers.map((item, i, arr) => {
                  const clickable = i !== arr.length - 1;
                  return (
                    <React.Fragment key={`${item._id}_${i}`}>
                      <BreadCrumbItem
                        clickable={clickable ? '1' : '0'}
                        onClick={
                          clickable
                            ? () => onBreadCrumbItemClick(
                              item.phoneNumber,
                              item.couponType,
                              i,
                            )
                            : null
                        }
                      >
                        {i > 0 && (
                          <Icon
                            margin="0 10px 0 0"
                            size="18px"
                            icon={Icons.RightArrowRegular}
                          />
                        )}
                        {item.phoneNumber}
                      </BreadCrumbItem>
                    </React.Fragment>
                  );
                })}
                <BreadcrumbClearButton>
                  <div onClick={() => clearBreadcrumb()}>
                    <Icon size={14} icon={Icons.TimesCircleSolid} />
                  </div>
                </BreadcrumbClearButton>
              </BreadcrumbWrapper>
            </BreadcrumbContainer>
          )}
          {app?.getSuMetricesQuery?.type && (
            <BreadcrumbContainer justify="flex-end" ref={breadcrumbRef}>
              <BreadcrumbWrapper padding="0 40px 0 0">
                <BreadcrumbHeading>
                  {app?.getSuMetricesQuery.type}
                </BreadcrumbHeading>
                {app?.getSuMetricesQuery?.subType && '-'}
                <BreadcrumbHeading>
                  {app?.getSuMetricesQuery.subType}
                </BreadcrumbHeading>
                <BreadcrumbHeading>
                  {app?.getSuMetricesQuery.end && app?.getSuMetricesQuery.start > 0
                    ? `${moment(app?.getSuMetricesQuery.start * 1000).format(
                      'L',
                    )} - ${moment(app?.getSuMetricesQuery.end * 1000).format(
                      'L',
                    )}`
                    : 'All Time'}
                </BreadcrumbHeading>
                <BreadcrumbClearButton>
                  <div onClick={() => clearBreadcrumb()}>
                    <Icon size={14} icon={Icons.TimesCircleSolid} />
                  </div>
                </BreadcrumbClearButton>
              </BreadcrumbWrapper>
            </BreadcrumbContainer>
          )}

          {isMobile ? (
            <ContactsList
              contacts={filteredContactsData}
              fetchMore={fetchMore}
              pagination={pagination}
              isMore={isMore}
              ordering={ordering}
              handleSort={handleSort}
              loadMoreData={loadMoreData}
              isLoadingMoreData={isLoadingMoreData}
              getRefferedContactsClick={getRefferedContactsClick}
              fetching={fetching || refLoading}
              setFetching={setFetching}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              refFetchMore={fetchMore}
              filterApplied={filterApplied}
              loading={allContactsLoading}
              scrollToTop={scrollToTop}
            />
          ) : (
            <CustomerTable
              contacts={filteredContactsData}
              fetchMore={fetchMore}
              pagination={pagination}
              isMore={isMore}
              ordering={ordering}
              handleSort={handleSort}
              loadMoreData={loadMoreData}
              isLoadingMoreData={isLoadingMoreData}
              getRefferedContactsClick={getRefferedContactsClick}
              fetching={fetching || refLoading}
              setFetching={setFetching}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              refFetchMore={refFetchMore}
              loading={allContactsLoading}
              filterApplied={filterApplied}
              scrollToTop={scrollToTop}
            />
          )}
        </Section>
      </Container>
    </>
  );
}
